import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import styles from "./appointmentPicker.module.css";
import { tWithKeyAttr as t } from "../../i18nHelper";
import { useAppSelector } from "../../services/hooks";
import { userSelector } from "../../services/selectors/user.selectors";
import { useLazyGetTherapistAppointmentsQuery } from "../../services/api/user.api";
import { TAppointment } from "../../services/types/user.types";
import i18n from "../../i18n";
import DatePicker, { DateObject } from "react-multi-date-picker";

interface AppointmentPickerProps {
  patientId: number;
  patientName: string;
  onSelect: (appointmentId: number, startTime?: string) => void;
  onSkip: () => void;
  titleKey?: string;
  subtitleKey?: string;
  confirmLabelKey?: string;
  skipLabelKey?: string;
}

// ---- date helpers ----

const DAY_NAMES_EN = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DAY_NAMES_HE = ["א׳", "ב׳", "ג׳", "ד׳", "ה׳", "ו׳", "ש׳"];

function getWeekStart(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay(); // 0=Sun
  d.setDate(d.getDate() - day);
  return d;
}

function addDays(date: Date, n: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function toYMD(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function formatDateLabel(d: Date, lang: string): string {
  return d.toLocaleDateString(lang === "he" ? "he-IL" : "en-US", { day: "numeric", month: "short" });
}

// ---- component ----

const AppointmentPicker: React.FC<AppointmentPickerProps> = ({
  patientId,
  patientName,
  onSelect,
  onSkip,
  titleKey = "APPT_PICKER_TITLE",
  subtitleKey = "APPT_PICKER_SUBTITLE",
  confirmLabelKey = "APPT_PICKER_LINK_AND_CONTINUE",
  skipLabelKey = "APPT_PICKER_SKIP",
}) => {
  const therapist = useAppSelector(userSelector);
  const [triggerFetch] = useLazyGetTherapistAppointmentsQuery();

  const today = useMemo(() => { const d = new Date(); d.setHours(0,0,0,0); return d; }, []);
  const [weekStart, setWeekStart] = useState(() => getWeekStart(today));
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  // Cache: month key → TAppointment[] filtered to this patient
  const cacheRef = useRef<Record<string, TAppointment[]>>({});

  // Appointments for the visible week
  const [weekAppointments, setWeekAppointments] = useState<TAppointment[]>([]);

  const lang = i18n.language;
  const isRtl = i18n.dir() === "rtl";
  const dayNames = lang === "he" ? DAY_NAMES_HE : DAY_NAMES_EN;

  // Months that overlap with the current week view
  const neededMonths = useMemo(() => {
    const weekEnd = addDays(weekStart, 6);
    const months: { month: number; year: number }[] = [];
    months.push({ month: weekStart.getMonth() + 1, year: weekStart.getFullYear() });
    if (weekEnd.getMonth() !== weekStart.getMonth() || weekEnd.getFullYear() !== weekStart.getFullYear()) {
      months.push({ month: weekEnd.getMonth() + 1, year: weekEnd.getFullYear() });
    }
    return months;
  }, [weekStart]);

  const fetchMonths = useCallback(async (months: { month: number; year: number }[]) => {
    if (!therapist.guid) return;
    const toFetch = months.filter(m => !cacheRef.current[`${m.year}-${m.month}`]);
    if (toFetch.length === 0) return;

    setLoading(true);
    try {
      const results = await Promise.all(
        toFetch.map(m =>
          triggerFetch({ therapistGuid: String(therapist.guid), month: m.month, year: m.year }).unwrap()
            .then(res => ({ key: `${m.year}-${m.month}`, appointments: res?.appointments || [] }))
            .catch(() => ({ key: `${m.year}-${m.month}`, appointments: [] as TAppointment[] }))
        )
      );
      for (const r of results) {
        cacheRef.current[r.key] = r.appointments.filter(
          (a: TAppointment) => a.usersId === patientId
        );
      }
    } finally {
      setLoading(false);
    }
  }, [therapist.guid, triggerFetch, patientId]);

  // Fetch when week changes
  useEffect(() => {
    fetchMonths(neededMonths);
  }, [neededMonths, fetchMonths]);

  // Derive visible appointments from cache
  useEffect(() => {
    const weekEnd = addDays(weekStart, 6);
    const all: TAppointment[] = [];
    for (const m of neededMonths) {
      const cached = cacheRef.current[`${m.year}-${m.month}`];
      if (cached) all.push(...cached);
    }
    // Filter to this week's range and sort
    const filtered = all.filter(a => {
      const d = new Date(a.appointmentDate);
      d.setHours(0, 0, 0, 0);
      return d >= weekStart && d <= weekEnd;
    });
    filtered.sort((a, b) => {
      const da = new Date(a.appointmentDate).getTime();
      const db = new Date(b.appointmentDate).getTime();
      if (da !== db) return da - db;
      return (a.startTime || "").localeCompare(b.startTime || "");
    });
    setWeekAppointments(filtered);
  }, [weekStart, neededMonths, loading]);

  // Pre-select closest appointment to now on initial load
  const preSelected = useRef(false);
  useEffect(() => {
    if (preSelected.current || weekAppointments.length === 0) return;
    // Only pre-select if we're viewing the current week
    if (!isSameDay(weekStart, getWeekStart(today))) return;

    const now = new Date();
    const nowMin = now.getHours() * 60 + now.getMinutes();
    let closest = weekAppointments[0];
    let closestDiff = Infinity;
    for (const a of weekAppointments) {
      if (isSameDay(new Date(a.appointmentDate), today) && a.startTime) {
        const [h, m] = a.startTime.split(":").map(Number);
        const diff = Math.abs(h * 60 + m - nowMin);
        if (diff < closestDiff) { closestDiff = diff; closest = a; }
      }
    }
    setSelectedId(closest.id);
    preSelected.current = true;
  }, [weekAppointments, weekStart, today]);

  // Days of the week
  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  }, [weekStart]);

  // Appointments grouped by day (only days that have appointments)
  const groupedByDay = useMemo(() => {
    const groups: { date: Date; items: TAppointment[] }[] = [];
    const map = new Map<string, TAppointment[]>();
    for (const a of weekAppointments) {
      const key = toYMD(new Date(a.appointmentDate));
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(a);
    }
    for (const [key, items] of map) {
      groups.push({ date: new Date(key + "T00:00:00"), items });
    }
    return groups;
  }, [weekAppointments]);

  // Days with appointments (for dot indicator)
  const daysWithAppts = useMemo(() => {
    const set = new Set<string>();
    for (const a of weekAppointments) set.add(toYMD(new Date(a.appointmentDate)));
    return set;
  }, [weekAppointments]);

  const handlePrevWeek = () => setWeekStart(prev => addDays(prev, -7));
  const handleNextWeek = () => setWeekStart(prev => addDays(prev, 7));

  const handleDateJump = (dateObj: DateObject | DateObject[] | null) => {
    if (!dateObj || Array.isArray(dateObj)) return;
    const d = dateObj.toDate();
    if (!isNaN(d.getTime())) setWeekStart(getWeekStart(d));
  };

  const handleToday = () => setWeekStart(getWeekStart(today));

  const handleConfirm = () => {
    if (selectedId == null) return;
    const appt = weekAppointments.find(a => a.id === selectedId);
    onSelect(selectedId, appt?.startTime);
  };

  return (
    <div className={styles.container} dir={i18n.dir()}>
      <h3 className={styles.title}>{t(titleKey)}</h3>
      <p className={styles.subtitle}>{t(subtitleKey)}</p>

      {/* Week Navigation */}
      <div className={styles.weekNav}>
        <button className={styles.navArrow} onClick={handlePrevWeek} aria-label="Previous week">
          ‹
        </button>
        <div className={styles.weekDays}>
          {weekDays.map((day, i) => {
            const isToday = isSameDay(day, today);
            const hasAppt = daysWithAppts.has(toYMD(day));
            const isActive = weekAppointments.some(
              a => isSameDay(new Date(a.appointmentDate), day) && a.id === selectedId
            );
            return (
              <div
                key={i}
                className={`${styles.dayCell} ${isActive ? styles.dayCellActive : ""} ${isToday ? styles.dayCellToday : ""}`}
                onClick={() => {
                  // If this day has appointments, select the first
                  const dayAppts = weekAppointments.filter(a => isSameDay(new Date(a.appointmentDate), day));
                  if (dayAppts.length > 0) setSelectedId(dayAppts[0].id);
                }}
              >
                <span className={styles.dayName}>{dayNames[i]}</span>
                <span className={styles.dayNumber}>{day.getDate()}</span>
                {hasAppt ? <span className={styles.dayDot} /> : <span className={styles.dayDotEmpty} />}
              </div>
            );
          })}
        </div>
        <button className={styles.navArrow} onClick={handleNextWeek} aria-label="Next week">
          ›
        </button>
      </div>

      {/* Date Jump */}
      <div className={styles.dateJump}>
        <DatePicker
          value={new DateObject(weekStart)}
          onChange={handleDateJump}
          format="DD/MM/YYYY"
          className={isRtl ? "rmdp-rtl" : ""}
          calendarPosition={isRtl ? "bottom-right" : "bottom-left"}
          inputClass={styles.dateInput}
          editable={false}
        />
        <button className={styles.todayBtn} onClick={handleToday}>
          {t("APPT_PICKER_TODAY")}
        </button>
      </div>

      {/* Appointments List */}
      {loading ? (
        <div className={styles.loading}>
          <div className={styles.spinner} />
          <div>{t("APPT_PICKER_LOADING")}</div>
        </div>
      ) : weekAppointments.length === 0 ? (
        <div className={styles.emptyWeek}>
          <div className={styles.emptyIcon}>📅</div>
          <div>{t("APPT_PICKER_NO_APPOINTMENTS")}</div>
        </div>
      ) : (
        <div className={styles.appointmentsList}>
          {groupedByDay.map(group => (
            <div key={toYMD(group.date)} className={styles.dayGroup}>
              <div className={styles.dayGroupLabel}>
                {isSameDay(group.date, today)
                  ? t("APPT_PICKER_TODAY_LABEL")
                  : formatDateLabel(group.date, lang)}
              </div>
              {group.items.map(appt => {
                const sel = selectedId === appt.id;
                return (
                  <div
                    key={appt.id}
                    className={`${styles.appointmentItem} ${sel ? styles.appointmentItemSelected : ""}`}
                    onClick={() => setSelectedId(appt.id)}
                  >
                    <span className={styles.appointmentTime}>{appt.startTime || "—"}</span>
                    <div className={styles.appointmentInfo}>
                      <div className={styles.appointmentType}>
                        {appt.treatmentGroupName || t("APPT_PICKER_APPOINTMENT")}
                      </div>
                    </div>
                    {sel && <span className={styles.checkMark}>✓</span>}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className={styles.actions}>
        <button
          className={styles.linkBtn}
          onClick={handleConfirm}
          disabled={selectedId == null}
        >
          {t(confirmLabelKey)}
        </button>
        <button className={styles.skipBtn} onClick={onSkip}>
          {t(skipLabelKey)}
        </button>
      </div>
    </div>
  );
};

export default AppointmentPicker;
